import { useConfigStore } from './useConfigStore'
import { z } from 'zod'

export const designVariantSchema = z.union([
  z.literal('google'),
  z.literal('msn'),
  z.literal('quartz'),
])

export type DesignVariant = z.infer<typeof designVariantSchema>

const homePageLayoutSchema = z.object({
  doodle: z.union([z.literal('off'), z.literal('on'), z.literal('random')]),
  design: z.optional(designVariantSchema),
  searchboxDesign: z.optional(
    z.union([
      z.literal('default'),
      z.literal('v2'),
      z.literal('v2-big-yolo'),
      z.literal('v3'),
    ])
  ),
  searchSuggestionType: z.optional(
    z.union([z.literal('simple'), z.literal('rich')])
  ),
  /**
   * Isolated from auto layout, these are sections which will live in the /Sections directory inside the code.
   * These will be self contained units that can be dynamically placed
   * above auto layout content by specifying the name of the section to
   * insert via the value of the aboveFoldSectionShown feature flag.
   */
  aboveFoldSectionShown: z.optional(
    z.union([
      z.literal('LargeCardWithGridSection'),
      z.literal('HalfPageAdWithGridSection'),
      z.literal('ContentNewsMixSection'),
      z.literal('ContentNewsMixWithKeywordSection'),
      z.literal('CarouselWithGridSection'),
      z.literal('GamesSection'),
      z.literal('CategoryMixSection'),
      z.literal('FiveColumn2x2Card'),
    ])
  ),
  higherRankInFixedSlots: z.optional(z.boolean()),
  removeSearchBox: z.optional(z.boolean()),
  centeredLogo: z.optional(z.boolean()),
  largeFooter: z.optional(z.boolean()),
})

export const pageLayoutSettingsSchema = z.object({
  homePage: z.optional(homePageLayoutSchema.partial()),
})

export type LayoutSettings = z.infer<typeof pageLayoutSettingsSchema>

export const useLayoutSettingsStore = useConfigStore<LayoutSettings>({
  defaults: pageLayoutSettingsSchema.parse({
    homePage: {
      doodle: 'off',
      design: 'msn',
      searchboxDesign: 'default',
      searchSuggestionType: 'simple',
      aboveFoldSectionShown: undefined,
    },
  }),
  configStoreKey: 'layoutSettings',
  schema: (data: unknown) => pageLayoutSettingsSchema.parse(data),
})
